import { useEffect } from 'react';

/**
 * Горячие клавиши плеера (в стиле YouTube): play/pause, перемотка, громкость,
 * mute, fullscreen, loop, переход к проценту ролика. События глобальные,
 * но игнорируются, когда фокус в полях ввода.
 *
 * Запись громкости — только через setVolumeState/setMutedState родителя:
 * сначала стейт/рефлы, потом элемент, чтобы синхронный volumechange от записи
 * видел совпадение и не откатывал значение.
 */
export const useVideoHotkeys = ({
	videoRef,
	hotkeys,
	duration,
	togglePlay,
	skip,
	toggleMute,
	toggleFullscreen,
	toggleLoop,
	setVolumeState,
	setMutedState,
}) => {
	useEffect(() => {
		if (!hotkeys) return;
		const onKeyDown = (e) => {
			const video = videoRef.current;
			if (!video) return;
			if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;

			switch (e.key) {
				case ' ':
				case 'k':
				case 'K':
					e.preventDefault();
					togglePlay();
					break;
				case 'ArrowLeft':
					e.preventDefault();
					skip(-1);
					break;
				case 'ArrowRight':
					e.preventDefault();
					skip(1);
					break;
				case 'ArrowUp':
					e.preventDefault();
					if (video.volume < 1) {
						const nextVolume = Math.min(video.volume + 0.1, 1);
						setVolumeState(nextVolume);
						setMutedState(false);
						video.volume = nextVolume;
						video.muted = false;
					}
					break;
				case 'ArrowDown':
					e.preventDefault();
					if (video.volume > 0) {
						const nextVolume = Math.max(video.volume - 0.1, 0);
						setVolumeState(nextVolume);
						video.volume = nextVolume;
					}
					break;
				case 'm':
				case 'M':
					e.preventDefault();
					toggleMute();
					break;
				case 'f':
				case 'F':
					e.preventDefault();
					toggleFullscreen();
					break;
				case 'l':
				case 'L':
					e.preventDefault();
					toggleLoop();
					break;
				case 'Home':
					e.preventDefault();
					video.currentTime = 0;
					break;
				case 'End':
					e.preventDefault();
					video.currentTime = duration;
					break;
				default:
					if (/^[0-9]$/.test(e.key)) {
						e.preventDefault();
						const percent = Number(e.key) * 0.1;
						video.currentTime = duration * percent;
					}
					break;
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [hotkeys, videoRef, togglePlay, skip, toggleMute, toggleFullscreen, toggleLoop, duration, setVolumeState, setMutedState]);
};
